import { useMemo } from 'react'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import type { AdvisorFeedback } from '@ppwr/convex/players/types'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import {
  canSetActionAdvisorFeedback,
  getActionAdvisorAvailability,
  getScopedActionAdvisorFeedback,
  hasActionAdvisorActor
} from './actionAdvisor.helpers'
import { type ActionAdvisorScope, useActionAdvisorStore } from './useActionAdvisorStore'

type UseActionAdvisorArgs = {
  gameId?: Id<'games'>
  handNumber?: number
  mode?: string
  userId?: string
}

export function useActionAdvisor({ gameId, handNumber, mode, userId }: UseActionAdvisorArgs) {
  const advisorConfig = useRemoteConfig((state) => state.advisor_config)
  const enabled = useActionAdvisorStore((state) => state.enabled)
  const feedbackEnabled = useActionAdvisorStore((state) => state.feedbackEnabled)
  const feedback = useActionAdvisorStore((state) => state.feedback)
  const feedbackScope = useActionAdvisorStore((state) => state.feedbackScope)
  const feedbackUpdatedAt = useActionAdvisorStore((state) => state.feedbackUpdatedAt)
  const setEnabled = useActionAdvisorStore((state) => state.setEnabled)
  const setFeedbackEnabled = useActionAdvisorStore((state) => state.setFeedbackEnabled)
  const setFeedbackState = useActionAdvisorStore((state) => state.setFeedback)
  const clearFeedback = useActionAdvisorStore((state) => state.clear)
  const clearFeedbackForScope = useActionAdvisorStore((state) => state.clearForScope)

  const scope = useMemo<ActionAdvisorScope>(
    () => ({
      gameId,
      handNumber,
      userId
    }),
    [gameId, handNumber, userId]
  )
  const hasActor = hasActionAdvisorActor(scope)

  const availability = getActionAdvisorAvailability({
    configEnabled: advisorConfig.enabled,
    enabled,
    feedbackEnabled,
    hasActor,
    mode,
    supportedModes: advisorConfig.supportedModes
  })

  const scopedFeedback = getScopedActionAdvisorFeedback({
    feedback,
    feedbackScope,
    feedbackUpdatedAt,
    scope
  })

  const advisorContext = availability.canRequestFeedback
    ? {
        enabled: true,
        supportedModes: advisorConfig.supportedModes
      }
    : undefined

  /** Returns the advice outcome the grade resolved ('followed' / 'deviated')
   * when the player acted with turn-start advice pending for that spot — see
   * the store's `setFeedback` for the full contract. */
  const setFeedback = (
    nextFeedback: AdvisorFeedback,
    nextScope: ActionAdvisorScope = scope,
    updatedAt = Date.now(),
    priorAdvice?: AdvisorFeedback
  ): 'followed' | 'deviated' | undefined => {
    if (
      !canSetActionAdvisorFeedback({
        enabled: useActionAdvisorStore.getState().enabled,
        feedbackEnabled: useActionAdvisorStore.getState().feedbackEnabled,
        scope: nextScope
      })
    ) {
      return undefined
    }

    return setFeedbackState(nextFeedback, nextScope, updatedAt, priorAdvice)
  }

  return {
    ...availability,
    enabled,
    feedbackEnabled,
    feedback: scopedFeedback,
    advisorContext,
    scope,
    setEnabled,
    setFeedbackEnabled,
    setFeedback,
    clearFeedback,
    clearFeedbackForScope: () => clearFeedbackForScope(scope)
  }
}
